// src/components/Widgets/MarketingWidget.jsx
import React, { useMemo, useState } from "react";
import WidgetHeader from "../Common/WidgetHeader";
import { useMarketing } from "../../contexts/MarketingContext";

const money = (v, currency = "AUD", locale = "en-AU") => {
  const n = Number(v) || 0;
  try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n); }
  catch { return `A$${n.toFixed(2)}`; }
};

export default function MarketingWidget({
  backTo = "/dashboard",
  currency = "AUD",
  locale = "en-AU",
  budgetThreshold = 500, // highlight expensive lines
}) {
  const { campaigns = [], monthlyTotal = 0, addCampaign, removeCampaign, updateCampaign, clearAll } = useMarketing();
  const [platform, setPlatform] = useState("");
  const [cost, setCost] = useState("");

  const canAdd = platform.trim().length > 0 && !Number.isNaN(Number(cost)) && Number(cost) >= 0;

  const sorted = useMemo(
    () => [...campaigns].sort((a, b) => String(a.platform).localeCompare(String(b.platform))),
    [campaigns]
  );

  const onAdd = () => {
    if (!canAdd) return;
    addCampaign({ platform: platform.trim(), cost: Number(cost) || 0 });
    setPlatform("");
    setCost("");
  };

  return (
    <div className="space-y-4">
      <WidgetHeader title="Marketing & Ads" to={backTo} />

      {/* Quick add */}
      <div className="flex gap-2" role="form" aria-label="Add campaign">
        <input
          className="flex-1 px-2 py-1 rounded bg-white/10 border border-white/10 text-sm"
          placeholder="Platform (e.g., Instagram, Google)"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          aria-label="Platform"
        />
        <input
          className="w-32 px-2 py-1 rounded bg-white/10 border border-white/10 text-sm"
          placeholder="Cost / mo"
          inputMode="decimal"
          type="number"
          min="0"
          step="0.01"
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          aria-label="Monthly cost"
        />
        <button
          className={`px-3 py-1 rounded text-sm text-white transition-colors ${!canAdd ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ backgroundColor: canAdd ? 'var(--color-primary)' : 'rgba(128, 128, 128, 0.6)' }}
          onClick={onAdd}
          disabled={!canAdd}
        >
          Add
        </button>
      </div>

      {/* List */}
      {sorted.length > 0 ? (
        <>
          <ul className="space-y-2 text-sm" aria-live="polite">
            {sorted.map((item) => {
              const over = Number(item.cost) > budgetThreshold;
              return (
                <li
                  key={item.id}
                  className={`flex items-center justify-between border-b border-white/10 pb-1 ${over ? "bg-red-800/20" : ""}`}
                >
                  <span className="font-medium truncate">{item.platform}</span>
                  <div className="flex items-center gap-2">
                    <input
                      className="w-24 px-2 py-0.5 rounded bg-white/10 border border-white/10 text-xs"
                      value={String(item.cost ?? 0)}
                      inputMode="decimal"
                      type="number"
                      min="0"
                      step="0.01"
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        updateCampaign(item.id, { cost: Number.isFinite(next) ? next : 0 });
                      }}
                      aria-label={`Cost for ${item.platform}`}
                    />
                    <span className="text-orange-300 tabular-nums">{money(item.cost, currency, locale)} /mo</span>
                    <button
                      className="px-2 py-0.5 rounded bg-red-600 text-white text-xs"
                      onClick={() => removeCampaign(item.id)}
                      aria-label={`Remove ${item.platform}`}
                      title="Remove"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between pt-2 border-t border-white/20 font-semibold">
            <span>Total Marketing Spend</span>
            <span className="text-green-400 tabular-nums">{money(monthlyTotal, currency, locale)}</span>
          </div>

          <div className="text-right">
            <button className="text-xs opacity-70 hover:opacity-100 underline" onClick={clearAll}>
              Clear all
            </button>
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-white/80">
          No marketing campaigns yet — add your first above.
        </div>
      )}
    </div>
  );
}
